import { formatUserFacingError } from '@/lib/errors'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, ExternalLink, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { issueRefundFn } from '@/lib/firebase/functions'
import { useAdminEventTickets } from '../hooks/useAdminEventTickets'
import { formatPrice } from '../components/TicketTypeSelector'
import type { TicketOrderDocument } from '@/types'

export function AdminEventTicketsPage() {
  const { id: eventId = '' } = useParams<{ id: string }>()
  const { orders, tickets, loading, error, refresh } = useAdminEventTickets(eventId)

  const [refundTarget, setRefundTarget] = useState<(TicketOrderDocument & { id: string }) | null>(null)
  const [refundDialogOpen, setRefundDialogOpen] = useState(false)
  const [refunding, setRefunding] = useState(false)
  const [refundError, setRefundError] = useState<string | null>(null)

  // Summary stats
  const totalSold = tickets.filter((t) => t.status !== 'refunded').length
  const totalRevenue = orders
    .filter((o) => o.status === 'paid' || o.status === 'partial_refund')
    .reduce((sum, o) => sum + o.total, 0)
  const checkedInCount = tickets.filter((t) => t.checkedIn).length

  async function handleRefund() {
    if (!refundTarget) return
    setRefunding(true)
    setRefundError(null)
    try {
      await issueRefundFn({ orderId: refundTarget.id, ticketIds: [] })
      refresh()
      setRefundDialogOpen(false)
      setRefundTarget(null)
    } catch (err: unknown) {
      setRefundError(formatUserFacingError(err, 'Refund failed'))
    } finally {
      setRefunding(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-4">
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin/events">
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            Back to Events
          </Link>
        </Button>
        <h1 className="text-3xl font-bold">Event Tickets</h1>
        <Button variant="ghost" size="sm" onClick={refresh} className="ml-auto">
          <RefreshCw className="h-4 w-4 mr-1.5" />
          Refresh
        </Button>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-lg border bg-card p-4 text-center">
          <p className="text-3xl font-bold">{totalSold}</p>
          <p className="text-sm text-muted-foreground">Tickets Sold</p>
        </div>
        <div className="rounded-lg border bg-card p-4 text-center">
          <p className="text-3xl font-bold">{formatPrice(totalRevenue)}</p>
          <p className="text-sm text-muted-foreground">Total Revenue</p>
        </div>
        <div className="rounded-lg border bg-card p-4 text-center">
          <p className="text-3xl font-bold">{checkedInCount}</p>
          <p className="text-sm text-muted-foreground">Checked In</p>
        </div>
      </div>

      {/* Orders table */}
      <div className="space-y-3">
        <h2 className="text-xl font-semibold">Orders</h2>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Purchaser</TableHead>
                <TableHead>Tickets</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>PDF</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No orders yet.
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell>
                      <p className="font-medium">
                        {order.purchaserFirstName} {order.purchaserLastName}
                      </p>
                      <p className="text-xs text-muted-foreground">{order.purchaserEmail}</p>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-0.5">
                        {order.items.map((item, i) => (
                          <p key={i} className="text-xs text-muted-foreground">
                            {item.ticketTypeName} × {item.quantity}
                          </p>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>{formatPrice(order.total)}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          order.status === 'paid'
                            ? 'default'
                            : order.status === 'refunded'
                              ? 'destructive'
                              : order.status === 'partial_refund'
                                ? 'secondary'
                                : 'outline'
                        }
                      >
                        {order.status === 'paid'
                          ? 'Paid'
                          : order.status === 'refunded'
                            ? 'Refunded'
                            : order.status === 'partial_refund'
                              ? 'Partial Refund'
                              : 'Pending'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {order.pdfUrl ? (
                        <a
                          href={order.pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs underline hover:text-primary"
                        >
                          PDF <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {order.status === 'paid' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => {
                            setRefundTarget(order)
                            setRefundDialogOpen(true)
                          }}
                        >
                          Refund
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Tickets table */}
      <div className="space-y-3">
        <h2 className="text-xl font-semibold">Tickets</h2>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Attendee</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Checked In</TableHead>
                <TableHead>Checked In At</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tickets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    No tickets yet.
                  </TableCell>
                </TableRow>
              ) : (
                tickets.map((ticket) => (
                  <TableRow key={ticket.id}>
                    <TableCell>
                      <p className="font-medium text-sm">{ticket.purchaserName}</p>
                      <p className="text-xs text-muted-foreground">{ticket.purchaserEmail}</p>
                    </TableCell>
                    <TableCell className="text-sm">{ticket.ticketTypeName}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          ticket.status === 'valid'
                            ? 'default'
                            : ticket.status === 'used'
                              ? 'secondary'
                              : 'destructive'
                        }
                      >
                        {ticket.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={ticket.checkedIn ? 'default' : 'outline'}>
                        {ticket.checkedIn ? 'Yes' : 'No'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {ticket.checkedInAt
                        ? ticket.checkedInAt.toDate().toLocaleString()
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Refund dialog */}
      <Dialog open={refundDialogOpen} onOpenChange={setRefundDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Issue Refund</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Refund order for{' '}
              <span className="font-semibold text-foreground">
                {refundTarget?.purchaserFirstName} {refundTarget?.purchaserLastName}
              </span>
              ? This will refund{' '}
              <span className="font-semibold text-foreground">
                {refundTarget ? formatPrice(refundTarget.total) : ''}
              </span>{' '}
              and mark all tickets as refunded.
            </p>
            {refundError && <p className="text-sm text-destructive">{refundError}</p>}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRefundDialogOpen(false)
                setRefundTarget(null)
                setRefundError(null)
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleRefund()}
              disabled={refunding}
            >
              {refunding ? 'Processing…' : 'Issue Refund'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
